import { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../services/api';

const AuthContext = createContext(null);

const REMEMBER_DAYS = 30;

function encodeCredentials(username, token) {
  return btoa(JSON.stringify({ username, token }));
}

function decodeCredentials(encoded) {
  try {
    return JSON.parse(atob(encoded));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoLogin, setAutoLogin] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    const rememberToken = localStorage.getItem('remember_token');
    const tokenExpiry = localStorage.getItem('token_expiry');

    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
    }

    if (rememberToken && tokenExpiry) {
      const expiry = parseInt(tokenExpiry, 10);
      if (Date.now() < expiry) {
        const creds = decodeCredentials(rememberToken);
        if (creds) {
          localStorage.setItem('token', creds.token);
          auth.me().then(res => {
            setUser(res.data);
            localStorage.setItem('user', JSON.stringify(res.data));
            setAutoLogin(true);
            setLoading(false);
          }).catch(() => {
            localStorage.removeItem('remember_token');
            localStorage.removeItem('token_expiry');
            setLoading(false);
          });
          return;
        }
      } else {
        localStorage.removeItem('remember_token');
        localStorage.removeItem('token_expiry');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password, rememberMe = false) => {
    const res = await auth.login(username, password);
    const { token, user } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    if (rememberMe) {
      const expiry = Date.now() + REMEMBER_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem('remember_token', encodeCredentials(username, token));
      localStorage.setItem('token_expiry', expiry.toString());
    }
    
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('remember_token');
    localStorage.removeItem('token_expiry');
    setUser(null);
    setAutoLogin(false);
  };

  const clearRememberMe = () => {
    localStorage.removeItem('remember_token');
    localStorage.removeItem('token_expiry');
    setAutoLogin(false);
  };

  const isAdmin = user?.tier === 0;
  const isDirector = user?.tier === 1;
  const isManager = user?.tier === 2;
  const isPromoter = user?.tier === 3;
  const isOperations = user?.tier === 4;

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, autoLogin, clearRememberMe, isAdmin, isDirector, isManager, isPromoter, isOperations }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
